import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveComponent as _resolveComponent, normalizeClass as _normalizeClass, createVNode as _createVNode, withCtx as _withCtx, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "analysis-viewer" }
const _hoisted_2 = ["innerHTML"]
const _hoisted_3 = ["innerHTML"]
const _hoisted_4 = /*#__PURE__*/_createElementVNode("div", { class: "decision-tooltips" }, null, -1)

import { ChatLineSquare } from '@element-plus/icons-vue';
import { debounce } from 'debounce';
import { computed, nextTick, onBeforeUnmount, onMounted, PropType, ref, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { Analysis } from '@/Analysis/analysis.entity';
import { EntrySelection } from '@/Entry/entry.entity';
import EntryService from '@/Entry/entry.service';
import {
    activateSelection,
    clearHighlightedTexts,
    computeTooltipPosition,
    deactivateSelection,
    highlightTexts,
    refreshTooltips,
} from '@/highlight';
import { Criterion } from '@/Litigation/criterion.entity';
import { useAnalysisStore } from '@/stores/analysis';


export default /*#__PURE__*/_defineComponent({
  __name: 'AnalysisViewer',
  props: {
    analysis: {
        type: Object as PropType<Analysis>,
        required: true,
    },
    annotatedEntry: {
        type: Object as PropType<{ criterion: Criterion; occurrence: Number }>,
        required: true,
    },
},
  emits: ['create-selection'],
  setup(__props, { expose: __expose, emit }) {

const props = __props;



const { t } = useI18n();
const analysisStore = useAnalysisStore();

const isTooltipModeActive = ref(true);
const highlightsLayers = ref(0);



const { analysis, annotatedEntry } = toRefs(props);

const isSelectionModeActive = computed((): boolean => {
    return annotatedEntry.value.criterion !== null;
});

const toggleTooltipMode = (forcedValue = null): void => {
    if (forcedValue !== null) {
        isTooltipModeActive.value = forcedValue;
    } else {
        isTooltipModeActive.value = !isTooltipModeActive.value;
    }

    highlightAllSelections();
};

const tooltipButtonLabel = computed((): string => {
    return isTooltipModeActive.value ? t('analysis.tooltipSelectionButtonOff') : t('analysis.tooltipSelectionButtonOn');
});

const clearOnSelections = async (): Promise<void> => {
    clearHighlightedTexts();
    highlightsLayers.value = 0;
    await nextTick();
};

const highlightAllSelections = async (): Promise<void> => {
    await clearOnSelections();

    if (isTooltipModeActive.value) {
        highlightTexts(analysis.value.entries, async (newHighlightsLayers) => {
            highlightsLayers.value = newHighlightsLayers;
            await nextTick();
        });
    }
};

const scrollToSelection = (criterion: Criterion, occurrence: number, selection: EntrySelection): void => {
    const mark = document.querySelector(
        `mark[data-code="${criterion.code}"][data-occurrence="${occurrence}"][data-start="${selection.startAt}"][data-end="${selection.endAt}"]`,
    );

    const { top } = computeTooltipPosition(mark as HTMLElement);
    window.scrollTo({ top });
    setTimeout(() => {
        activateSelection(criterion.code, occurrence);
        setTimeout(() => {
            deactivateSelection(criterion.code, occurrence);
        }, 500);
    }, 500);
};

__expose({ scrollToSelection, highlightAllSelections });

const computeSelectionPosition = (range: Range): { startAt: number; endAt: number } => {
    const getNodeLength = (node: Node) => {
        return node.textContent?.length || 0;
    };

    const getPreviousSiblingsLength = (node: Node) => {
        let length = 0;
        let currentNode = node;
        while (currentNode.previousSibling) {
            length += getNodeLength(currentNode.previousSibling);
            currentNode = currentNode.previousSibling;
        }
        return length;
    };

    const getParentPreviousSiblingsLength = (node: Node) => {
        let length = 0;
        let currentNode = node;

        const rootElement = document.getElementsByClassName('decision-text')[0];

        let isRootElement = false;
        while (currentNode.parentNode && !isRootElement) {
            length += getPreviousSiblingsLength(currentNode.parentNode);
            currentNode = currentNode.parentNode;
            isRootElement = (currentNode as HTMLParagraphElement) === rootElement;
        }

        return length;
    };

    const positions = { startAt: 0, endAt: 0 };
    ['start', 'end'].forEach((boundary) => {
        const positionNode = range[`${boundary}Container` as keyof typeof range] as Node;
        let position = 0;
        position += range[`${boundary}Offset` as keyof typeof range] as number;
        position += getPreviousSiblingsLength(positionNode);
        position += getParentPreviousSiblingsLength(positionNode);
        positions[`${boundary}At` as keyof { startAt: number; endAt: number }] = position;
    });

    return positions;
};

const getSelection = (): Selection | null => {
    const currentSelection = window.getSelection();
    return currentSelection !== null && !currentSelection.isCollapsed ? currentSelection : null;
};

const addToCurrentSelections = async (): Promise<void> => {
    const currentSelection = getSelection();
    if (!isSelectionModeActive.value || !currentSelection) {
        return;
    }

    const range = currentSelection.getRangeAt(0);

    const { startAt, endAt } = computeSelectionPosition(range);

    const entryService = new EntryService();
    await entryService.addSelection(analysis.value, annotatedEntry.value.criterion, annotatedEntry.value.occurrence, {
        startAt,
        endAt,
        text: currentSelection.toString().trim(),
    });

    emit('create-selection', currentSelection.toString().trim());
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const debounceRefreshTooltips = debounce(function debounceRefreshTooltips(this: any) {
    refreshTooltips();
}, 1000);

onMounted(async () => {
    analysisStore.markAsViewed(analysis.value.analysisId);

    await highlightAllSelections();
    window.addEventListener('resize', async () => {
        await debounceRefreshTooltips();
    });

    document.getElementsByClassName('decision-text')[0].addEventListener(
        'mouseup',
        () => {
            addToCurrentSelections();
        },
        false,
    );
});

onBeforeUnmount(async () => {
    window.removeEventListener('resize', async () => {
        await debounceRefreshTooltips();
    });
});

return (_ctx: any,_cache: any) => {
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_badge = _resolveComponent("el-badge")!
  const _component_el_divider = _resolveComponent("el-divider")!
  const _component_el_space = _resolveComponent("el-space")!
  const _component_el_affix = _resolveComponent("el-affix")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_component_el_affix, null, {
      default: _withCtx(() => [
        _createVNode(_component_el_space, null, {
          default: _withCtx(() => [
            _createVNode(_component_el_badge, {
              "is-dot": isTooltipModeActive.value,
              type: "primary"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_button, {
                  type: "primary",
                  icon: _unref(ChatLineSquare),
                  plain: "",
                  class: _normalizeClass(['is-small']),
                  title: tooltipButtonLabel.value,
                  onClick: _cache[0] || (_cache[0] = ($event: any) => (toggleTooltipMode()))
                }, null, 8, ["icon", "title"])
              ]),
              _: 1
            }, 8, ["is-dot"]),
            _createVNode(_component_el_divider)
          ]),
          _: 1
        })
      ]),
      _: 1
    }),
    _createElementVNode("div", {
      class: "is-flex-1 decision-content",
      onScroll: _cache[1] || (_cache[1] = ($event: any) => (_unref(debounceRefreshTooltips)())),
      onMousedown: _cache[2] || (_cache[2] = ($event: any) => (toggleTooltipMode(false))),
      onMouseup: _cache[3] || (_cache[3] = ($event: any) => (toggleTooltipMode(true)))
    }, [
      _createElementVNode("p", {
        class: _normalizeClass(['decision-text', 'pr-1', { 'has-text-cursor': _unref(annotatedEntry).criterion !== null }]),
        innerHTML: _unref(analysis).decision.content
      }, null, 10, _hoisted_2),
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(highlightsLayers.value, (i) => {
        return (_openBlock(), _createElementBlock("p", {
          key: i,
          class: _normalizeClass(['decision-highlight-layer', 'pr-1']),
          innerHTML: _unref(analysis).decision.content
        }, null, 8, _hoisted_3))
      }), 128)),
      _hoisted_4
    ], 32)
  ]))
}
}

})